<!-- Polaris (fase 5.5): a company of the list, on the kit's plant (areas/contacts/CompaniesList.vue). `layout="row"`
     is a `ui/table` row (from md up), `layout="item"` the compact phone item (the contacts rule, R2-5-PC-14). The
     whole row opens the company. Columns are the woot card's: name ("Unnamed company" when empty), domain, contacts
     and the last activity (R2-5-PC-10 = A). -->
<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useNumberFormatter } from 'shared/composables/useNumberFormatter';
import { dynamicTime } from 'shared/helpers/timeHelper';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import {
  TableCell,
  TableRow,
} from 'dashboard/components-next/polaris/ui/table';

const props = defineProps({
  id: { type: Number, required: true },
  name: { type: String, default: '' },
  domain: { type: String, default: '' },
  contactsCount: { type: Number, default: 0 },
  avatarUrl: { type: String, default: '' },
  lastActivityAt: { type: [String, Number], default: null },
  // The company open in the drawer (aria-current, the table's selected look).
  isOpen: { type: Boolean, default: false },
  layout: {
    type: String,
    default: 'row',
    validator: value => ['row', 'item'].includes(value),
  },
  // A picture of a row (the faded empty state): not focusable, not clickable.
  inert: { type: Boolean, default: false },
});

const emit = defineEmits(['showCompany']);

const { t, locale } = useI18n();
// The viewer's locale (pt_BR → pt-BR); vue-i18n's n() hands 'pt_BR' to Intl and throws.
const { formatFullNumber: n } = useNumberFormatter();

const displayName = computed(() => props.name || t('COMPANIES.UNNAMED'));
const noValue = computed(() => t('CONTACTS_POLARIS.LIST.NO_VALUE'));

const lastActivity = computed(() =>
  props.lastActivityAt ? dynamicTime(props.lastActivityAt) : ''
);
const lastActivityDate = computed(() =>
  props.lastActivityAt
    ? new Date(props.lastActivityAt * 1000).toLocaleString(
        locale.value.replace('_', '-'),
        { dateStyle: 'full', timeStyle: 'short' }
      )
    : ''
);

const open = () => {
  if (!props.inert) emit('showCompany', props.id);
};
</script>

<template>
  <TableRow
    v-if="layout === 'row'"
    :tabindex="inert ? undefined : 0"
    class="outline-none focus-visible:bg-polaris-muted/50 focus-visible:ring-[3px] focus-visible:ring-inset focus-visible:ring-polaris-ring/50"
    :class="inert ? '' : 'cursor-pointer'"
    :data-state="isOpen ? 'selected' : undefined"
    :aria-current="isOpen ? 'true' : undefined"
    :data-company-row="id"
    @click="open"
    @keydown.enter.self.prevent="open"
  >
    <TableCell class="max-w-0 min-w-52 ps-4">
      <div class="flex min-w-0 items-center gap-3">
        <Avatar
          :name="displayName"
          :src="avatarUrl || ''"
          :size="32"
          hide-offline-status
          class="shrink-0"
        />
        <span
          class="truncate font-medium text-polaris-foreground"
          :title="displayName"
        >
          {{ displayName }}
        </span>
      </div>
    </TableCell>
    <TableCell class="hidden max-w-48 @[44rem]/list:table-cell">
      <span
        v-if="domain"
        class="block truncate text-polaris-muted-foreground"
        :title="domain"
      >
        {{ domain }}
      </span>
      <span v-else class="text-polaris-muted-foreground">{{ noValue }}</span>
    </TableCell>
    <TableCell class="tabular-nums text-polaris-muted-foreground">
      {{ n(contactsCount || 0) }}
    </TableCell>
    <TableCell
      class="hidden pe-4 text-polaris-muted-foreground @[31rem]/list:table-cell"
    >
      <time
        v-if="lastActivityAt"
        :title="lastActivityDate"
        :datetime="new Date(lastActivityAt * 1000).toISOString()"
      >
        {{ lastActivity }}
      </time>
      <template v-else>{{ noValue }}</template>
    </TableCell>
  </TableRow>

  <li
    v-else
    class="flex items-center gap-3 border-0 border-b border-solid border-polaris-border px-4 py-3 outline-none focus-visible:ring-[3px] focus-visible:ring-inset focus-visible:ring-polaris-ring/50 data-[state=selected]:bg-polaris-muted"
    :class="inert ? '' : 'cursor-pointer'"
    :tabindex="inert ? undefined : 0"
    :data-state="isOpen ? 'selected' : undefined"
    :aria-current="isOpen ? 'true' : undefined"
    :data-company-item="id"
    @click="open"
    @keydown.enter.self.prevent="open"
  >
    <Avatar
      :name="displayName"
      :src="avatarUrl || ''"
      :size="32"
      hide-offline-status
      class="shrink-0"
    />
    <div class="grid min-w-0 flex-1 gap-0.5">
      <div class="flex min-w-0 items-baseline gap-2">
        <span
          class="min-w-0 flex-1 truncate font-medium type-polaris-body text-polaris-foreground"
          :title="displayName"
        >
          {{ displayName }}
        </span>
        <time
          v-if="lastActivityAt"
          class="shrink-0 type-polaris-caption text-polaris-muted-foreground"
          :title="lastActivityDate"
        >
          {{ lastActivity }}
        </time>
      </div>
      <span class="truncate type-polaris-caption text-polaris-muted-foreground">
        {{ domain || noValue }}
        <span aria-hidden="true">{{ t('CONTACTS_POLARIS.DRAWER.DOT') }}</span>
        {{
          t(
            'COMPANIES.CONTACTS_COUNT',
            { n: contactsCount || 0 },
            contactsCount || 0
          )
        }}
      </span>
    </div>
  </li>
</template>
